// src/pages/ChatBot/query-logs/TriageModal.tsx
//
// Triage dialog for a query-log row: toggle eval-set membership, mark the
// served answer as incorrect, or permanently delete the log. The PATCH body
// carries only the fields the admin actually changed (the backend rejects an
// empty body), and Save stays disabled until a toggle differs from the row.

import { useState } from 'react';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { useModalBackdropClose } from '../../../hooks/useModalBackdropClose';
import { deleteQueryLog, patchQueryLogAudit, QueryLogApiError } from './api';
import { formatDateTime } from './derive';
import { statusTone, flagTone } from './chip-tones';
import Tag from '../shared/Tag';
import type { ProjectedQueryLog } from './types';
import './TriageModal.css';

interface TriageModalProps {
  log: ProjectedQueryLog | null;
  onClose: () => void;
  onChanged: () => void;
}

type AuditPatch = { evalSet?: boolean; feedback?: -1 | 0 };

// The Incorrect toggle drives the thumbs signal: checking sends feedback -1 (thumbs-down),
// unchecking sends 0 (clear). A user's thumbs-up (+1) is left alone unless the admin
// explicitly checks the toggle over it.
function buildChangedFields(
  log: ProjectedQueryLog,
  evalSet: boolean,
  incorrect: boolean,
): AuditPatch {
  const changed: AuditPatch = {};
  if (evalSet !== (log.evalSet === true)) changed.evalSet = evalSet;
  if (incorrect !== (log.feedback === -1)) changed.feedback = incorrect ? -1 : 0;
  return changed;
}

function toErrorMessage(error: unknown, fallback: string): string {
  return error instanceof QueryLogApiError ? error.message : fallback;
}

export default function TriageModal({ log, onClose, onChanged }: TriageModalProps) {
  if (!log) return null;
  // Keyed by log id so all local state resets whenever a different row opens.
  return <TriageModalContent key={log.id} log={log} onClose={onClose} onChanged={onChanged} />;
}

function TriageModalContent({
  log,
  onClose,
  onChanged,
}: {
  log: ProjectedQueryLog;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [evalSet, setEvalSet] = useState(log.evalSet === true);
  const [incorrect, setIncorrect] = useState(log.feedback === -1);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBusy = isSaving || isDeleting;
  const changedFields = buildChangedFields(log, evalSet, incorrect);
  const hasChanges = Object.keys(changedFields).length > 0;

  const backdrop = useModalBackdropClose(() => {
    if (isBusy) return;
    onClose();
  });
  useEscapeClose(onClose, !isBusy);

  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const handleSave = async () => {
    if (!hasChanges || isBusy) return;
    setIsSaving(true);
    setError(null);
    try {
      await patchQueryLogAudit(log.id, changedFields);
      onChanged();
      onClose();
    } catch (err) {
      setError(toErrorMessage(err, 'Failed to save changes.'));
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (isBusy) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteQueryLog(log.id);
      onChanged();
      onClose();
    } catch (err) {
      setError(toErrorMessage(err, 'Failed to delete the query log.'));
      setIsDeleting(false);
    }
  };

  return (
    <div className="smrtdocs-modal-overlay" {...backdrop}>
      <div className="smrtdocs-modal" style={{ width: 'min(540px, 95vw)' }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Triage Query</h2>
          <button
            type="button"
            className="qlt-close"
            onClick={handleClose}
            disabled={isBusy}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="smrtdocs-modal-body">
          <div className="qlt-question">{log.question}</div>
          <p className="qlt-meta">
            {log.askedBy} · {formatDateTime(log.askedAt)}
          </p>

          <div className="qlt-badges">
            <Tag tone={statusTone(log.status.status)}>{log.status.status}</Tag>
            {log.flags.map((flag) => (
              <Tag key={flag.flag} tone={flagTone(flag)}>
                {flag.flag}
              </Tag>
            ))}
          </div>

          <p className="qlt-outcome">
            Served: {log.outcome.served}
            {log.outcome.generation ? ` · generation ${log.outcome.generation}` : ''}
          </p>

          {log.errorDescription && (
            <div className="qlt-error-box">
              <span className="qlt-micro-label">Error</span>
              {log.errorDescription}
            </div>
          )}

          <div className="qlt-toggles">
            <label className="qlt-toggle-row">
              <input
                type="checkbox"
                checked={evalSet}
                onChange={(e) => setEvalSet(e.target.checked)}
                disabled={isBusy}
              />
              <span className="qlt-toggle-text">
                <span className="qlt-toggle-name">Eval set</span>
                <span className="qlt-toggle-help">
                  Include this query in the evaluation gold set.
                </span>
              </span>
            </label>
            <label className="qlt-toggle-row">
              <input
                type="checkbox"
                checked={incorrect}
                onChange={(e) => setIncorrect(e.target.checked)}
                disabled={isBusy}
              />
              <span className="qlt-toggle-text">
                <span className="qlt-toggle-name">Incorrect</span>
                <span className="qlt-toggle-help">The served answer was wrong.</span>
              </span>
            </label>
          </div>

          <div className="qlt-danger">
            <span className="qlt-micro-label">Danger zone</span>
            {isConfirmingDelete ? (
              <>
                <p className="qlt-danger-warning">
                  This permanently deletes the log
                  {log.hasCustomAnswer ? ' and its custom answer' : ''}. This cannot be
                  undone.
                </p>
                <div className="qlt-danger-actions">
                  <button
                    type="button"
                    className="qlt-btn-danger"
                    onClick={handleConfirmDelete}
                    disabled={isBusy}
                  >
                    {isDeleting ? 'Deleting...' : 'Confirm delete'}
                  </button>
                  <button
                    type="button"
                    className="qlt-btn-keep"
                    onClick={() => setIsConfirmingDelete(false)}
                    disabled={isBusy}
                  >
                    Keep
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                className="qlt-btn-danger"
                onClick={() => setIsConfirmingDelete(true)}
                disabled={isBusy}
              >
                Delete Query
              </button>
            )}
          </div>

          {error && <div className="qlt-error-banner">{error}</div>}

          <div className="qlt-footer">
            <button type="button" className="btn-cancel" onClick={handleClose} disabled={isBusy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-save"
              onClick={handleSave}
              disabled={!hasChanges || isBusy}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
